import { useEffect, useState, lazy, Suspense } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { BarChart3, FileText, Beer } from "lucide-react";
import ContentEditor from "./ContentEditor";
import { Stat, DataTable } from "./admin/_shared";
import AdminHeaderActions from "./admin/AdminHeaderActions";
import SchedulerHistory from "./admin/SchedulerHistory";
import EventsPanel from "./admin/EventsPanel";

// Lazy-load heavier tab panels — their bundle is fetched only when the
// admin actually opens the corresponding tab. This keeps the Admin page
// initial paint fast, especially on tablets.
const Analytics = lazy(() => import("./Analytics"));
const BeersPanel = lazy(() => import("./BeersPanel"));
const StaffPinsPanel = lazy(() => import("./StaffPinsPanel"));
const StaffPinRequestsPanel = lazy(() => import("./StaffPinRequestsPanel"));
const EventBookingsPanel = lazy(() => import("./admin/EventBookingsPanel"));
const TableReservationsPanel = lazy(() => import("./admin/TableReservationsPanel"));
const PrivateEventsPanel = lazy(() => import("./admin/PrivateEventsPanel"));
const ProductOrdersPanel = lazy(() => import("./admin/ProductOrdersPanel"));
const GiftCardsPanel = lazy(() => import("./admin/GiftCardsPanel"));
const PendingPaymentsPanel = lazy(() => import("./admin/PendingPaymentsPanel"));
const POSStripePanel = lazy(() => import("./admin/POSStripePanel"));
const PaymentsHealthPanel = lazy(() => import("./admin/PaymentsHealthPanel"));
const BackupsPanel = lazy(() => import("./admin/BackupsPanel"));
const PushCampaignPanel = lazy(() => import("./admin/PushCampaignPanel"));

const TabLoader = () => <div className="text-muted-foreground py-8">Chargement…</div>;

export default function Admin() {
  const { user } = useAuth();
  const isEditor = user?.role === "editor";
  const isAdmin = user?.role === "admin";
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [eb, setEb] = useState([]);
  const [tr, setTr] = useState([]);
  const [pe, setPe] = useState([]);
  const [users, setUsers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [giftCards, setGiftCards] = useState([]);
  const [productOrders, setProductOrders] = useState([]);
  const [schedulerStatus, setSchedulerStatus] = useState(null);

  const reload = () => {
    api.get("/events", { params: { include_past: true } }).then(({ data }) => setEvents(data)).catch(() => {});
    if (isAdmin) {
      api.get("/admin/dashboard").then(({ data }) => setStats(data)).catch(() => {});
      api.get("/admin/event-bookings").then(({ data }) => setEb(data)).catch(() => {});
      api.get("/admin/table-reservations").then(({ data }) => setTr(data)).catch(() => toast.error("Impossible de charger les réservations de tables — reconnectez-vous puis réessayez."));
      api.get("/admin/private-events").then(({ data }) => setPe(data)).catch(() => {});
      api.get("/admin/users").then(({ data }) => setUsers(data)).catch(() => {});
      api.get("/admin/newsletter-subscribers").then(({ data }) => setSubs(data)).catch(() => {});
      api.get("/admin/gift-cards").then(({ data }) => setGiftCards(data)).catch(() => {});
      api.get("/admin/product-orders").then(({ data }) => setProductOrders(data)).catch(() => {});
      api.get("/admin/scheduler-status").then(({ data }) => setSchedulerStatus(data)).catch(() => {});
    }
  };

  useEffect(() => {
    if (user) reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="admin-page">
      <div className="mb-10 flex items-start justify-between gap-6 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-primary mb-2">
            {isEditor ? "Espace éditeur" : "Backstage"}
          </div>
          <h1 className="font-display text-5xl">
            {isEditor ? "Édition du contenu" : "Tableau de bord"}
          </h1>
          {isEditor && (
            <p className="text-sm text-muted-foreground mt-2">
              Bonjour {user.name?.split(" ")[0]} — vous pouvez modifier les textes, images, la carte et les événements.
            </p>
          )}
        </div>
        {isAdmin && (
          <AdminHeaderActions schedulerStatus={schedulerStatus} onReload={reload} />
        )}
      </div>

      <SchedulerHistory status={schedulerStatus} />

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-10" data-testid="admin-stats">
          <Stat label="Événements" value={stats.events} />
          <Stat label="Réservations" value={stats.event_bookings} />
          <Stat label="Tables" value={stats.table_reservations} />
          <Stat label="Bar mobile" value={stats.private_events} />
          <Stat label="Clients" value={stats.users} />
          <Stat label="CA (€)" value={stats.revenue.toFixed(0)} />
        </div>
      )}

      <Tabs defaultValue={isEditor ? "content" : "analytics"} data-testid="admin-tabs">
        <TabsList className="bg-card border border-border flex-wrap h-auto">
          <TabsTrigger value="content" data-testid="admin-tab-content"><FileText className="w-3.5 h-3.5 mr-1.5" />Contenu</TabsTrigger>
          {isAdmin && <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 className="w-3.5 h-3.5 mr-1.5" />Analytics</TabsTrigger>}
          <TabsTrigger value="events" data-testid="admin-tab-events">Événements</TabsTrigger>
          {isAdmin && <TabsTrigger value="bookings" data-testid="admin-tab-bookings">Réservations</TabsTrigger>}
          {isAdmin && <TabsTrigger value="tables" data-testid="admin-tab-tables">Tables</TabsTrigger>}
          {isAdmin && <TabsTrigger value="private" data-testid="admin-tab-private">Bar mobile</TabsTrigger>}
          {isAdmin && <TabsTrigger value="users" data-testid="admin-tab-users">Clients</TabsTrigger>}
          {isAdmin && <TabsTrigger value="newsletter" data-testid="admin-tab-newsletter">Newsletter</TabsTrigger>}
          {isAdmin && <TabsTrigger value="gifts" data-testid="admin-tab-gifts">Cartes cadeaux</TabsTrigger>}
          {isAdmin && <TabsTrigger value="orders" data-testid="admin-tab-orders">Boutique</TabsTrigger>}
          {isAdmin && <TabsTrigger value="payments" data-testid="admin-tab-payments"><FileText className="w-3.5 h-3.5 mr-1.5" />Paiements</TabsTrigger>}
          {isAdmin && <TabsTrigger value="pos-stripe" data-testid="admin-tab-pos-stripe">💳 Stripe POS</TabsTrigger>}
          <TabsTrigger value="pos-beers" data-testid="admin-tab-pos-beers"><Beer className="w-3.5 h-3.5 mr-1.5" />Bières POS</TabsTrigger>
          {isAdmin && <TabsTrigger value="pos-pins" data-testid="admin-tab-pos-pins"><FileText className="w-3.5 h-3.5 mr-1.5" />PIN POS</TabsTrigger>}
          {isAdmin && <TabsTrigger value="push" data-testid="admin-tab-push">📣 Push</TabsTrigger>}
          {isAdmin && <TabsTrigger value="backups" data-testid="admin-tab-backups">💾 Backups</TabsTrigger>}
        </TabsList>

        <TabsContent value="content" className="mt-6">
          <ContentEditor />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="analytics" className="mt-6">
            <Suspense fallback={<TabLoader />}><Analytics /></Suspense>
          </TabsContent>
        )}

        <TabsContent value="events" className="mt-6">
          <EventsPanel events={events} onReload={reload} />
        </TabsContent>

        {isAdmin && (
          <TabsContent value="bookings" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <EventBookingsPanel bookings={eb} users={users} onReload={reload} />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="tables" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <TableReservationsPanel reservations={tr} onReload={reload} />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="private" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <PrivateEventsPanel requests={pe} onReload={reload} />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="users" className="mt-6">
            <h2 className="font-display text-2xl mb-4">Clients ({users.length})</h2>
            <DataTable rows={users} cols={[
              ["name", "Nom"],
              ["email", "Email"],
              ["phone", "Téléphone"],
              ["role", "Rôle"],
            ]} testid="users-table" />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="newsletter" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-display text-2xl">Newsletter ({subs.length})</h2>
              <a
                href={`data:text/csv;charset=utf-8,${encodeURIComponent("email,first_name,last_name,source,subscribed_at\n" + subs.map((s) => `${s.email},${s.first_name || ""},${s.last_name || ""},${s.source || ""},${s.subscribed_at || ""}`).join("\n"))}`}
                download="newsletter-saveurs-normandes.csv"
                className="text-sm text-primary hover:underline"
                data-testid="newsletter-export-csv"
              >
                Exporter CSV
              </a>
            </div>
            <DataTable rows={subs} cols={[
              ["email", "Email"],
              ["first_name", "Prénom"],
              ["last_name", "Nom"],
              ["source", "Source"],
              ["subscribed_at", "Date"],
            ]} testid="newsletter-table" />
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="gifts" className="mt-6">
            <h2 className="font-display text-2xl mb-4">Cartes cadeaux ({giftCards.length})</h2>
            {(() => {
              const active = giftCards.filter((g) => g.status === "active");
              const totalSold = giftCards.filter((g) => g.status !== "pending_payment").reduce((s, g) => s + (g.initial_amount || 0), 0);
              const totalUsed = giftCards.reduce((s, g) => s + ((g.initial_amount || 0) - (g.current_balance || 0)), 0);
              const liability = active.reduce((s, g) => s + (g.current_balance || 0), 0);
              return (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-testid="gift-cards-stats">
                  <Stat label="Actives" value={active.length} />
                  <Stat label="CA cartes" value={totalSold.toFixed(0) + " €"} />
                  <Stat label="Encaissé" value={totalUsed.toFixed(0) + " €"} />
                  <Stat label="Solde restant" value={liability.toFixed(0) + " €"} />
                </div>
              );
            })()}
            <DataTable rows={giftCards} cols={[
              ["code", "Code"],
              ["initial_amount", "Initial €"],
              ["current_balance", "Solde €"],
              ["recipient_email", "Destinataire"],
              ["status", "Statut"],
              ["created_at", "Date"],
            ]} testid="gift-cards-table" />
            <div className="mt-6">
              <Suspense fallback={<TabLoader />}>
                <GiftCardsPanel cards={giftCards} onReload={reload} />
              </Suspense>
            </div>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="orders" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <ProductOrdersPanel orders={productOrders} onReload={reload} />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="payments" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <PaymentsHealthPanel />
              <PendingPaymentsPanel />
            </Suspense>
          </TabsContent>
        )}
        {isAdmin && (
          <TabsContent value="pos-stripe" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <POSStripePanel />
            </Suspense>
          </TabsContent>
        )}

        <TabsContent value="pos-beers" className="mt-6">
          <Suspense fallback={<TabLoader />}><BeersPanel /></Suspense>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="pos-pins" className="mt-6 space-y-6">
            <Suspense fallback={<TabLoader />}>
              <StaffPinRequestsPanel />
              <StaffPinsPanel />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="backups" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <BackupsPanel />
            </Suspense>
          </TabsContent>
        )}

        {isAdmin && (
          <TabsContent value="push" className="mt-6">
            <Suspense fallback={<TabLoader />}>
              <PushCampaignPanel />
            </Suspense>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
